/**
 * Utility functions for passkey/WebAuthn support detection
 */

/**
 * Checks if the current browser and device support WebAuthn/Passkeys
 * @returns Promise<boolean> - true if passkeys are supported, false otherwise
 */
export const isPasskeySupported = async (): Promise<boolean> => {
  try {
    // Check if WebAuthn is available in the browser
    if (!window.PublicKeyCredential) {
      return false;
    }

    // Check if the browser supports conditional UI (passkey autofill)
    const conditionalUISupported = await PublicKeyCredential.isConditionalMediationAvailable?.();
    console.log('ConditionalUIsupported', conditionalUISupported);

    // Check if user verification is available (biometrics, PIN, etc.)
    const userVerifyingPlatformAuthenticatorAvailable =
      await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable?.();
    console.log('platformauthenticator', userVerifyingPlatformAuthenticatorAvailable);
    // Return true if both conditional UI and user verification are supported
    return Boolean(conditionalUISupported && userVerifyingPlatformAuthenticatorAvailable);
  } catch (error) {
    console.warn('Error checking passkey support:', error);
    return false;
  }
};

/**
 * Combined check for both device support and user enrollment
 * @param email - User's email address
 * @returns Promise<boolean> - true if both device supports passkeys AND user has them enrolled
 */
export const canUsePasskey = async (): Promise<boolean> => {
  try {
    const [deviceSupported] = await Promise.all([isPasskeySupported()]);
    console.log('ispasskeySupported', isPasskeySupported);

    return deviceSupported;
  } catch (error) {
    console.warn('Error checking passkey compatability:', error);
    return false;
  }
};

/**
 * Browser and platform specific passkey support detection
 * @returns object with detailed support information
 */
export const getPasskeySupportDetails = async () => {
  const details = {
    webAuthnSupported: Boolean(window.PublicKeyCredential),
    conditionalUISupported: false,
    userVerifyingPlatformAuthenticatorAvailable: false,
    browser: getBrowserInfo(),
    platform: getPlatformInfo(),
  };

  try {
    if (details.webAuthnSupported) {
      details.conditionalUISupported = (await PublicKeyCredential.isConditionalMediationAvailable?.()) || false;
      details.userVerifyingPlatformAuthenticatorAvailable =
        (await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable?.()) || false;
    }
  } catch (error) {
    console.warn('Error getting passkey support details:', error);
  }

  return details;
};

/**
 * Get browser information
 */
const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;

  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
  if (userAgent.includes('Edge')) return 'Edge';

  return 'Unknown';
};

/**
 * Get platform information
 */
const getPlatformInfo = () => {
  const userAgent = navigator.userAgent;

  if (userAgent.includes('Windows')) return 'Windows';
  if (userAgent.includes('Mac')) return 'macOS';
  if (userAgent.includes('Linux')) return 'Linux';
  if (userAgent.includes('Android')) return 'Android';
  if (userAgent.includes('iPhone') || userAgent.includes('iPad')) return 'iOS';

  return 'Unknown';
};
